// corte.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { format, parseISO, getDay, isSameDay, startOfWeek } from 'date-fns';
import { es } from 'date-fns/locale';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import {
  getCortes,
  createCorte,
  updateCorte,
  deleteCorte,
  getResumenPorSucursal,
  getResumenTotal,
  createCorteWithFiles,
  updateCorteWithFiles
} from '../api/authcorte';


/* ======== Role helpers (read-only for Contador) ======== */
function getUserRoleFromToken() {
  try {
    const token = localStorage.getItem('token') || localStorage.getItem('accessToken') ||
      sessionStorage.getItem('token') || sessionStorage.getItem('accessToken');
    if (!token || token.indexOf('.') === -1) return '';
    const payload = JSON.parse(atob(token.split('.')[1] || ''));
    const role = payload.rol || payload.role || '';
    return String(role || '').toLowerCase();
  } catch (e) { return ''; }
}

/* =========================================================
    Localizador date-fns para react-big-calendar
========================================================= */
const locales = {
  'es': es,
};

const localizer = dateFnsLocalizer({
  format,
  parse: (str) => parseISO(str),
  startOfWeek: (date) => startOfWeek(date, { weekStartsOn: 1, locale: es }),
  getDay,
  locales,
});

/* =========================================================
    Iconos (SVG puros)
========================================================= */
const IconCalendar = (props) => (
  <svg viewBox="0 0 24 24" className={props.className || 'h-5 w-5'} fill="none" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const IconAdd = (props) => (
  <svg viewBox="0 0 24 24" className={props.className || 'h-6 w-6'} fill="none" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
  </svg>
);

const IconChevronLeft = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className || 'h-5 w-5'} fill="none" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);
const IconChevronRight = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className || 'h-5 w-5'} fill="none" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);

/* =========================================================
    Estilos por sucursal + renderer del evento
========================================================= */
// 🟢 MEJORA: Paleta de colores más vibrante y moderna
const sucursalColors = {
  'la fe': { bg: '#DBEAFE', text: '#1E40AF', dot: '#2563EB' }, // Blue 100/800/600
  'expo': { bg: '#FEF3C7', text: '#92400E', dot: '#D97706' }, // Amber 100/800/600
  'colon': { bg: '#FCE7F3', text: '#9D174D', dot: '#DB2777' }, // Pink 100/800/600
  'torreon': { bg: '#DCFCE7', text: '#166534', dot: '#16A34A' }, // Green 100/800/600
  'pablo': { bg: '#F3E8FF', text: '#6B21A8', dot: '#9333EA' }, // Purple 100/800/600
  'reynosa': { bg: '#E0E7FF', text: '#3730A3', dot: '#4F46E5' }, // Indigo 100/800/600
  'tapachula': { bg: '#CCFBF1', text: '#115E59', dot: '#0D9488' }, // Teal 100/800/600
  'default': { bg: '#F1F5F9', text: '#334155', dot: '#64748B' }, // Slate 100/700/500
};

const SucursalPill = React.memo(({ sucursal, monto, estado, estado_contador, notas }) => {
  const key = (sucursal || '').toLowerCase();
  const c_sucursal = sucursalColors[key] || sucursalColors.default;

  let statusKey = 'incompleto';
  let icono = '❌';

  if (estado_contador === 'confirmado') {
    statusKey = 'confirmado';
    icono = '✅';
  } else if (estado === 'completo') {
    statusKey = 'completo_pendiente';
    icono = '⏳';
  }

  const statusColors = {
    incompleto: { bg: '#FFE4E6', text: '#9F1239', dot: '#E11D48' }, // Rose 100/800/600
    completo_pendiente: { bg: '#F3F4F6', text: '#4B5563', dot: '#6B7280' }, // Gray 100/600/500
    confirmado: { bg: '#DCFCE7', text: '#15803D', dot: '#16A34A' }, // Green 100/700/600
  };

  const c = statusColors[statusKey];

  // 🟢 NUEVO: Verificar si hay notas/observaciones
  const tieneNotas = notas && notas.trim().length > 0;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 9999,
      backgroundColor: c.bg, color: c.text,
      fontSize: 11, fontWeight: 600,
      border: `1px solid ${c.bg === '#FFFFFF' ? '#E2E8F0' : 'transparent'}`,
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      whiteSpace: 'nowrap'
    }}>
      {/* Indicador de sucursal */}
      <span style={{
        width: 8, height: 8, borderRadius: '50%',
        backgroundColor: c_sucursal.dot,
        boxShadow: `0 0 0 2px ${c.bg}` // Ring effect
      }} />

      <span style={{ textTransform: 'capitalize' }}>{sucursal || 'sucursal'}</span>
      <span style={{ opacity: 0.4 }}>|</span>
      <span>${Number(monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
      <span>{icono}</span>

      {/* 🟢 NUEVO: Mostrar icono de notas si existen */}
      {tieneNotas && (
        <span title="Tiene observaciones" style={{ fontSize: 12, marginLeft: 2 }}>📝</span>
      )}
    </div>
  );
});

const CustomEvent = React.memo(({ event }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <SucursalPill
      sucursal={event.sucursal}
      monto={event.cantidad_corte ?? event.cantidad}
      estado={event.estado}
      estado_contador={event.estado_contador}
      notas={event.notas || event.comentario}
    />
  </div>
));

/* =========================================================
    Métodos de pago base + helpers
========================================================= */
const METODOS_PAGO_BASE = [
  { metodo: 'Cheque', importe: '', completo: false },
  { metodo: '6 MSI', importe: '', completo: false },
  { metodo: 'Efectivo', importe: '', completo: false },
  { metodo: 'Por Definir', importe: '', completo: false },
  { metodo: 'Sin Forma de Cobro', importe: '', completo: false },
  { metodo: 'Tarjeta de Credito', importe: '', completo: false },
  { metodo: 'Tarjeta de Debito', importe: '', completo: false },
  { metodo: 'Transferencia', importe: '', completo: false },
];


const normalizePagos = (pagos) => {
  const safePagos = Array.isArray(pagos) ? pagos : (pagos && typeof pagos === 'object' ? Object.values(pagos) : []);
  const byMetodo = Object.fromEntries(safePagos.map(p => [p.metodo, p]));

  return METODOS_PAGO_BASE.map(base => {
    const existing = byMetodo[base.metodo];
    const importe = existing?.importe !== undefined ? existing.importe : '';
    const importeNumerico = parseFloat(importe || 0) || 0;

    // 🟢 MEJORA: Si el importe es 0 o vacío, automáticamente marcar como completo
    const completo = importeNumerico === 0 ? true : (existing?.completo || false);

    return {
      ...base,
      ...existing,
      importe,
      completo
    };
  });
};


const totalPagos = (arr = []) =>
  normalizePagos(arr).reduce((acc, p) => acc + (parseFloat(p.importe || 0) || 0), 0);

/* =========================================================
    Modal (fecha SOLO día)
========================================================= */
function CutModal({ event, slotInfo, onClose, onSave, selectedSucursal, onRefreshEvents }) {

  const __roleHere = getUserRoleFromToken();
  const __isContadorHere = __roleHere === 'contador';
  const __isAdminHere = __roleHere === 'admin';

  const [formData, setFormData] = useState({
    sucursal: '',
    fecha_corte: '',
    cantidad_corte: '',
    notas: '',
    estado: 'incompleto',
    estado_contador: 'pendiente',
    pagos: METODOS_PAGO_BASE,
    creado_por_nombre: null,
    confirmado_por_nombre: null,
    confirmado_en: null,
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [archivosAdjuntos, setArchivosAdjuntos] = useState([]);
  const [archivosExistentes, setArchivosExistentes] = useState([]);
  const [fileCategory, setFileCategory] = useState('evidencia');

  const [nuevaNota, setNuevaNota] = useState('');

  const determineCorteStatus = useCallback((pagos) => {
    const normalized = normalizePagos(pagos);
    const allComplete = normalized.every(p => !!p.completo);
    return allComplete ? 'completo' : 'incompleto';
  }, []);

  // === Archivos ===
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const category = fileCategory;

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif',
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];

    const validFiles = files.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        alert(`❌ El archivo "${file.name}" excede 10MB.`); return false;
      }
      if (!allowedTypes.includes(file.type)) {
        alert(`❌ El archivo "${file.name}" no es un formato permitido.`); return false;
      }
      return true;
    }).map(file => ({
      file,
      name: file.name,
      size: file.size,
      categoria: category
    }));

    const totalArchivos = archivosAdjuntos.length + validFiles.length + archivosExistentes.length;
    if (totalArchivos > 50) {
      alert(`❌ Máximo 10 archivos por corte. Solo se añadieron ${10 - archivosAdjuntos.length - archivosExistentes.length} archivos.`);
      const filesToAdd = validFiles.slice(0, 50 - archivosAdjuntos.length - archivosExistentes.length);
      if (filesToAdd.length > 0) setArchivosAdjuntos(prev => [...prev, ...filesToAdd]);
    } else if (validFiles.length > 0) {
      setArchivosAdjuntos(prev => [...prev, ...validFiles]);
    }

    e.target.value = null;
  };

  const handleFileCategoryChange = (e) => {
    setFileCategory(e.target.value);
  };

  const removerArchivo = (index) => setArchivosAdjuntos(prev => prev.filter((_, i) => i !== index));

  const eliminarArchivoExistente = async (nombreArchivo) => {
    if (!window.confirm('¿Eliminar este archivo?')) return;
    try {
      const token = localStorage.getItem('token');
      const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:3000' : window.location.origin;
      const response = await fetch(`${API_BASE}/api/cortes/archivo/${nombreArchivo}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setArchivosExistentes(prev => prev.filter(a => a.nombre_archivo !== nombreArchivo));
        setMessage('Archivo eliminado.');
      }
    } catch (error) { console.error(error); alert('Error al eliminar archivo.'); }
  };
  const descargarArchivo = async (archivo) => {
    const nombreArchivo = archivo.nombre_archivo || archivo.filename;
    const nombreOriginal = archivo.nombre_original || archivo.originalname || archivo.nombre || nombreArchivo;

    if (!nombreArchivo) { alert('No se puede descargar el archivo.'); return; }
    try {
      const token = localStorage.getItem('token');
      const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:3000' : window.location.origin;
      const response = await fetch(`${API_BASE}/api/cortes/archivo/${nombreArchivo}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Error al descargar archivo (HTTP ${response.status}):`, errorText);
        alert('Error al descargar el archivo.'); return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = nombreOriginal || `archivo_${nombreArchivo}`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error de red al descargar:', error);
      alert('Error de red al descargar el archivo.');
    }
  };

  const sucursales = [
    { value: 'la fe', label: 'La Fe' },
    { value: 'expo', label: 'Expo' },
    { value: 'colon', label: 'Colón' },
    { value: 'torreon', label: 'Torreón' },
    { value: 'pablo', label: 'Pablo' },
    { value: 'reynosa', label: 'Reynosa' },
    { value: 'tapachula', label: 'Tapachula' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const cargarArchivosExistentes = async (corteId) => {
    try {
      const token = localStorage.getItem('token');
      const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:3000' : window.location.origin;
      const response = await fetch(`${API_BASE}/api/cortes/${corteId}/archivos`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const archivos = await response.json();
        setArchivosExistentes(Array.isArray(archivos) ? archivos.map(a => ({
          categoria: a.categoria || 'evidencia',
          nombre_archivo: a.nombre_archivo,
          nombre_original: a.nombre_original || a.nombre,
          fecha_subida: a.fecha_subida,
          ...a
        })) : []);
      } else setArchivosExistentes([]);
    } catch (error) {
      console.error("Error cargando archivos:", error);
    }
  };

  useEffect(() => {
    if (event) {
      setIsEditing(false);
      const sucursalValue = Array.isArray(event.sucursal) ? event.sucursal[0] : event.sucursal;
      const base = event.dia ? parseISO(event.dia) : parseISO(event.fecha);

      const pagosData = event.formas_pago || event.pagos;
      const pagos = normalizePagos(pagosData);

      setFormData({
        cantidad_corte: event.cantidad_corte ?? event.cantidad,
        sucursal: sucursalValue,
        fecha_corte: format(base, 'yyyy-MM-dd'),
        pagos,
        estado: event.estado || determineCorteStatus(pagos),
        estado_contador: event.estado_contador || 'pendiente',
        creado_por_nombre: event.creado_por_nombre || null,
        confirmado_por_nombre: event.confirmado_por_nombre || null,
        confirmado_en: event.confirmado_en || null,
        notas: event.notas || event.comentario || '',
      });

      if (event.id) cargarArchivosExistentes(event.id);
      setArchivosAdjuntos([]);
      setNuevaNota('');
    } else if (slotInfo) {
      setIsEditing(true);
      setFormData(prev => {
        const initialPagos = normalizePagos([]);
        return {
          ...prev,
          sucursal: selectedSucursal !== 'todas' ? selectedSucursal : '',
          fecha_corte: format(slotInfo.start, 'yyyy-MM-dd'),
          cantidad_corte: '',
          notas: '',
          estado: determineCorteStatus(initialPagos),
          estado_contador: 'pendiente',
          pagos: initialPagos,
          creado_por_nombre: null,
          confirmado_por_nombre: null,
          confirmado_en: null,
        };
      });
      setArchivosAdjuntos([]);
      setArchivosExistentes([]);
      setNuevaNota('');
    }
  }, [event, slotInfo, selectedSucursal, determineCorteStatus]);


  const updatePago = (index, key, value) => {
    setFormData(prev => {
      const current = Array.isArray(prev.pagos) && prev.pagos.length > 0 ? prev.pagos : normalizePagos([]);

      const metodoObjetivo = current[index]?.metodo || METODOS_PAGO_BASE[index].metodo;
      let nextPagos = current;

      nextPagos = current.map(p =>
        p.metodo === metodoObjetivo
          ? { ...p, [key]: key === 'importe' ? value : !!value }
          : p
      );

      const updatedPagoIndex = nextPagos.findIndex(p => p.metodo === metodoObjetivo);
      if (updatedPagoIndex !== -1) {
        const updatedPago = nextPagos[updatedPagoIndex];

        let importeNumerico = 0;
        if (key === 'importe') {
          // 🟢 MEJORA: Tratar string vacío como 0
          importeNumerico = value === '' ? 0 : (Number(value) || 0);
        } else {
          importeNumerico = parseFloat(updatedPago.importe || 0) || 0;
        }

        if (key === 'importe') {
          // 🟢 MEJORA: Auto-marcar como completo si es 0 o vacío
          if (importeNumerico === 0 || value === '') {
            nextPagos[updatedPagoIndex] = { ...updatedPago, completo: true };
          } else {
            nextPagos[updatedPagoIndex] = { ...updatedPago, completo: false };
          }
        }
      }

      const newStatus = determineCorteStatus(nextPagos);

      return { ...prev, pagos: nextPagos, estado: newStatus };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    const fechaISO = new Date(formData.fecha_corte).toISOString();
    const total = totalPagos(formData.pagos);
    const estadoFinal = formData.estado;
    const estadoContadorFinal = formData.estado_contador;

    const archivosMetadata = archivosAdjuntos.map(a => ({
      nombre_original: a.name,
      categoria: a.categoria
    }));

    const comentarioFinal = (formData.notas || '') +
      (nuevaNota
        ? `\n\n--- [${__roleHere.toUpperCase()} | ${new Date().toLocaleString('es-MX')}] ---\n${nuevaNota}`
        : '');

    const backendData = {
      dia: formData.fecha_corte,
      cantidad: total,
      comentario: comentarioFinal,
      responsable: event?.responsable || 'Usuario',
      fecha: fechaISO,
      sucursal: [formData.sucursal],
      estado: estadoFinal,
      estado_contador: estadoContadorFinal,
      formas_pago: normalizePagos(formData.pagos).map(p => ({
        metodo: p.metodo,
        importe: parseFloat(p.importe || 0) || 0,
        completo: !!p.completo
      })),
      archivosMetadata: archivosMetadata,
      confirmado_por_nombre: formData.confirmado_por_nombre
    };

    const createFormData = (data, files) => {
      const form = new FormData();
      form.append('corteData', JSON.stringify(data));
      files.forEach(item => {
        form.append('archivos', item.file, item.name);
      });
      return form;
    };


    try {
      if (event) {
        if (archivosAdjuntos.length > 0 && !__isContadorHere) {
          const formDataToSubmit = createFormData(backendData, archivosAdjuntos);
          await updateCorteWithFiles(event.id, formDataToSubmit);
          setMessage('Corte y archivos actualizados.');
          setNuevaNota('');
        } else {
          await updateCorte(event.id, backendData);
          setMessage('Corte actualizado.');
          setNuevaNota('');
        }
        if (onRefreshEvents) await onRefreshEvents();
      } else {
        if (archivosAdjuntos.length > 0 && !__isContadorHere) {
          const formDataToSubmit = createFormData(backendData, archivosAdjuntos);
          const corteCreado = await createCorteWithFiles(formDataToSubmit);
          setMessage('Corte y archivos creados exitosamente.');
          setNuevaNota('');
          setIsEditing(false);
          setArchivosAdjuntos([]);

          const base = corteCreado.dia ? parseISO(corteCreado.dia) : parseISO(corteCreado.fecha);
          setFormData(prev => ({
            ...prev,
            sucursal: '',
            cantidad_corte: corteCreado.cantidad,
            notas: corteCreado.comentario,
            sucursal: Array.isArray(corteCreado.sucursal) ? corteCreado.sucursal[0] : corteCreado.sucursal,
            pagos: normalizePagos(corteCreado.formas_pago || []),
            fecha_corte: format(base, 'yyyy-MM-dd'),
            estado: corteCreado.estado,
            estado_contador: corteCreado.estado_contador || 'pendiente',
            creado_por_nombre: corteCreado.creado_por_nombre || null,
            confirmado_por_nombre: corteCreado.confirmado_por_nombre || null,
            confirmado_en: corteCreado.confirmado_en || null,
          }));

          if (onRefreshEvents) await onRefreshEvents();
          if (corteCreado.id) setTimeout(async () => { await cargarArchivosExistentes(corteCreado.id); }, 500);

          setTimeout(onSave, 600);
          return;
        } else {
          const corteCreado = await createCorte(backendData);
          setMessage('Corte creado exitosamente.');
          setNuevaNota('');

          if (onRefreshEvents) await onRefreshEvents();

          setTimeout(onSave, 600);
          return;
        }
      }
      setTimeout(onSave, 600);
    } catch (error) {
      console.error(error);
      setMessage('Error al guardar el corte.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!event) return;
    if (__isContadorHere) { setMessage('No tienes permisos para eliminar.'); return; }
    if (!window.confirm(`¿Eliminar el corte ID: ${event.id}?`)) return;
    setLoading(true);
    try {
      await deleteCorte(event.id);
      if (onRefreshEvents) await onRefreshEvents();
      onClose();
    } catch (error) {
      console.error(error);
      setMessage('Error al eliminar.');
    } finally {
      setLoading(false);
    }
  };

  if (!event && !slotInfo) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={onClose}>
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" />
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="relative w-full max-w-2xl rounded-2xl border border-white/10 bg-white shadow-2xl"
          onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-900 to-blue-700 px-6 py-5 sticky top-0 z-10 rounded-t-2xl">
            <h3 className="text-lg font-semibold text-white">
              {event ? `Detalle de Corte #${event.id}` : 'Nuevo Corte de Caja'}
            </h3>
            <p className="text-blue-100 text-sm mt-1">
              {event ? 'Visualiza y gestiona la información del corte' : 'Registra un nuevo corte diario'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {message && (
              <div className={`p-4 rounded-xl text-sm font-medium animate-fade-in ${message.includes('Error') ? 'bg-red-50 text-red-700 border border-red-100' : 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                }`}>
                {message}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Sucursal */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Sucursal</label>
                {isEditing ? (
                  <select name="sucursal" value={formData.sucursal} onChange={handleChange} required
                    className="w-full rounded-xl border-slate-200 focus:ring-2 focus:ring-blue-500/20">
                    <option value="">Seleccionar sucursal...</option>
                    {sucursales.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                ) : (
                  <div className="w-full rounded-xl border-slate-200 bg-slate-50 px-3 py-2 text-slate-700 font-medium capitalize">
                    {formData.sucursal}
                  </div>
                )}
              </div>

              {/* Fecha */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Fecha del corte</label>
                {isEditing ? (
                  <input type="date" name="fecha_corte" value={formData.fecha_corte} onChange={handleChange} required
                    className="w-full rounded-xl border-slate-200 focus:ring-2 focus:ring-emerald-500/20" />
                ) : (
                  <div className="w-full rounded-xl border-slate-200 bg-slate-50 px-3 py-2 text-slate-700">
                    {formData.fecha_corte
                      ? format(parseISO(formData.fecha_corte), 'EEEE d MMMM yyyy', { locale: es })
                      : '-'}
                  </div>
                )}
              </div>
            </div>

            {/* Tabla de Pagos */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-2">Desglose de Formas de Pago</label>
              <div className="overflow-hidden rounded-xl border border-slate-200 shadow-sm">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium">Método</th>
                      <th className="px-4 py-3 text-right font-medium">Importe</th>
                      <th className="px-4 py-3 text-center font-medium w-24">Completo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {formData.pagos.map((p, i) => (
                      <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-4 py-2.5 text-slate-700 font-medium">{p.metodo}</td>
                        <td className="px-4 py-2.5 text-right">
                          {isEditing ? (
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                              <input
                                type="number"
                                step="0.01"
                                className="w-32 rounded-lg border-slate-200 pl-7 py-1.5 text-right text-sm focus:ring-2 focus:ring-blue-500/20"
                                value={p.importe}
                                onChange={(e) => updatePago(i, 'importe', e.target.value)}
                                placeholder="0.00"
                              />
                            </div>
                          ) : (
                            <span className={parseFloat(p.importe) > 0 ? 'font-semibold text-slate-900' : 'text-slate-400'}>
                              ${parseFloat(p.importe || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          {isEditing ? (
                            <div className="flex justify-center">
                              <input
                                type="checkbox"
                                className="h-4 w-4"
                                checked={!!p.completo}
                                disabled={(parseFloat(p.importe || 0) || 0) === 0}
                                onChange={(e) => updatePago(i, 'completo', e.target.checked)}
                              />
                            </div>
                          ) : (
                            <span>{p.completo ? '✅' : '❌'}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-slate-50 font-semibold text-slate-900">
                      <td className="px-4 py-3">Total</td>
                      <td className="px-4 py-3 text-right text-blue-600">
                        ${totalPagos(formData.pagos).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Notas (Historial) */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Notas (Historial)</label>
                {isEditing ? (
                  <div>
                    {/* Historial (Read-only) */}
                    <textarea
                      readOnly
                      rows="4"
                      value={formData.notas || 'Sin observaciones previas'}
                      className="w-full rounded-xl border-slate-200 bg-slate-50 px-3 py-2 text-slate-500 text-xs mb-2"
                      style={{ whiteSpace: 'pre-wrap' }}
                    />
                    {/* Nueva Nota */}
                    <label className="block text-xs font-medium text-slate-600 mb-1 pt-2">Añadir nuevo comentario</label>
                    <textarea
                      name="nuevaNota"
                      value={nuevaNota}
                      onChange={(e) => setNuevaNota(e.target.value)}
                      rows="3"
                      placeholder="Escribe una nueva observación aquí..."
                      className="w-full rounded-xl border-slate-200 focus:ring-2 focus:ring-emerald-500/20"
                    />
                  </div>
                ) : (
                  <textarea
                    readOnly
                    rows="5"
                    value={formData.notas || 'Sin observaciones'}
                    className="w-full rounded-xl border-slate-200 bg-slate-50 px-3 py-2 text-slate-700 min-h-[72px] whitespace-pre-wrap"
                    style={{ whiteSpace: 'pre-wrap' }}
                  />
                )}
              </div>

              {/* Estado del Contador */}
              {(__isContadorHere || __isAdminHere) && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Estado (Contador)</label>
                  {isEditing ? (
                    <select
                      name="estado_contador"
                      value={formData.estado_contador || 'pendiente'}
                      onChange={handleChange}
                      className={`w-full rounded-xl border-slate-200 font-medium focus:ring-2 ${formData.estado_contador === 'confirmado'
                        ? 'bg-green-50 text-green-700 focus:ring-green-500/20'
                        : 'bg-amber-50 text-amber-700 focus:ring-amber-500/20'
                        }`}
                    >
                      <option value="pendiente">⏳ Pendiente de Revisión</option>
                      <option value="confirmado">✅ Confirmado</option>
                    </select>
                  ) : (
                    <div className={`w-full rounded-xl border-slate-200 px-3 py-2 font-medium ${formData.estado_contador === 'confirmado'
                      ? 'bg-green-50 text-green-700'
                      : 'bg-amber-50 text-amber-700'
                      }`}>
                      {formData.estado_contador === 'confirmado' ? '✅ Confirmado' : '⏳ Pendiente de Revisión'}
                    </div>
                  )}

                  {formData.estado_contador === 'confirmado' && formData.confirmado_por_nombre && (
                    <div className="text-xs text-slate-500 mt-2 p-2 bg-green-50 rounded-lg">
                      Confirmado por: <strong>{formData.confirmado_por_nombre}</strong>
                      <br />
                      Fecha: {formData.confirmado_en ? format(parseISO(formData.confirmado_en), 'dd/MM/yyyy HH:mm') : '-'}
                    </div>
                  )}
                </div>
              )}

              {/* Estado (Automático) */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Estado del corte (Automático)</label>
                <div className={`w-full rounded-xl border-slate-200 px-3 py-2 font-medium ${formData.estado === 'completo' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                  }`}>
                  {formData.estado === 'completo' ? '✅ Corte Completo' : '❌ Corte Incompleto'}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Se marca completo automáticamente cuando todos los métodos con importe &gt; 0 están marcados como "Completo".
                </p>
              </div>
              {/* Quién Creó (Bitácora) */}
              {(event || formData.creado_por_nombre) && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Registro Creado Por</label>
                  <div className="text-sm text-slate-700 bg-slate-50 px-3 py-2 rounded-xl border border-slate-200">
                    👤 {formData.creado_por_nombre || 'Desconocido'}
                  </div>
                </div>
              )}

              {/* Archivos */}
              <div className="border-t pt-4">
                <label className="block text-xs font-medium text-slate-600 mb-3">
                  📎 Archivos Adjuntos <span className="text-slate-400 ml-2">(Máximo 50 archivos, 10MB cada uno)</span>
                </label>

                {/* Archivos Existentes */}
                {archivosExistentes.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-slate-500 mb-2">Archivos actuales:</p>
                    <div className="space-y-2">
                      {archivosExistentes.map((archivo, index) => {
                        const isCobranza = (archivo.categoria || 'evidencia') === 'cobranza';
                        const displaySize = (archivo.size || archivo.tamaño) ?
                          `${((archivo.size || archivo.tamaño) / 1024 / 1024).toFixed(2)} MB` :
                          'Tamaño no disponible';

                        return (
                          <div key={index} className={`flex items-center justify-between p-3 rounded-lg border ${isCobranza ? 'bg-yellow-50 border-yellow-200' : 'bg-blue-50 border-blue-200'
                            }`}>
                            <div className="flex items-center gap-3">
                              <span className={isCobranza ? 'text-yellow-600' : 'text-blue-600'}>📄</span>
                              <div>
                                <p className="text-sm font-medium text-slate-700 truncate max-w-[200px]" title={archivo.nombre_original}>
                                  {archivo.nombre_original}
                                </p>
                                <div className="flex gap-2 text-xs text-slate-500">
                                  <span>{displaySize}</span>
                                  <span>•</span>
                                  <span className="capitalize">{isCobranza ? 'Cobranza' : 'Evidencia'}</span>
                                  <span>•</span>
                                  <span>{archivo.fecha_subida ? format(parseISO(archivo.fecha_subida), 'dd/MM/yy HH:mm') : ''}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button type="button" onClick={() => descargarArchivo(archivo)} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                                Ver
                              </button>

                              {isEditing && !__isContadorHere && (
                                <button type="button" onClick={() => eliminarArchivoExistente(archivo.nombre_archivo)} className="text-red-600 hover:text-red-800 text-sm font-medium ml-2">
                                  Eliminar
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Archivos Nuevos */}
                {isEditing && !__isContadorHere && archivosAdjuntos.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-slate-500 mb-2">Archivos nuevos a subir:</p>
                    <div className="space-y-2">
                      {archivosAdjuntos.map((archivo, index) => {
                        const isCobranza = archivo.categoria === 'cobranza';
                        return (
                          <div key={index} className={`flex items-center justify-between p-3 rounded-lg border ${isCobranza ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'
                            }`}>
                            <div className="flex items-center gap-3">
                              <span className={isCobranza ? 'text-amber-600' : 'text-green-600'}>📄</span>
                              <div>
                                <p className="text-sm font-medium text-slate-700 truncate max-w-[200px]">
                                  {archivo.name}
                                </p>
                                <div className="flex gap-2 text-xs text-slate-500">
                                  <span>{(archivo.size / 1024 / 1024).toFixed(2)} MB</span>
                                  <span>•</span>
                                  <span className="capitalize">{isCobranza ? 'Cobranza' : 'Evidencia'}</span>
                                </div>
                              </div>
                            </div>
                            <button type="button" onClick={() => removerArchivo(index)} className="text-slate-400 hover:text-red-500">
                              ✕
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {isEditing && !__isContadorHere && (
                  <div className="mt-3">
                    <div className="flex gap-3 mb-2 items-center">
                      <label className="text-xs font-medium text-slate-600">Categoría del archivo:</label>
                      <select
                        value={fileCategory}
                        onChange={handleFileCategoryChange}
                        className="text-xs rounded-lg border-slate-200 py-1 pl-2 pr-8 focus:ring-2 focus:ring-blue-500/20"
                      >
                        <option value="evidencia">EVIDENCIA (General)</option>
                        <option value="cobranza">COBRANZA (Pagos Pendientes)</option>
                      </select>
                    </div>

                    <input
                      type="file"
                      id="archivos"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <label htmlFor="archivos" className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-slate-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 cursor-pointer transition-colors">
                      <div className="text-center">
                        <IconAdd className="mx-auto h-6 w-6 text-slate-400 mb-1" />
                        <span className="text-sm text-slate-600 font-medium">Click para subir archivos</span>
                        <p className="text-xs text-slate-400 mt-1">PDF, Imágenes, Word, Excel (Max 10MB)</p>
                      </div>
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t bg-slate-50 px-6 py-4 flex items-center justify-between sticky bottom-0 z-10 rounded-b-2xl">
              <div className="flex items-center gap-2">

                {event && isEditing && !__isContadorHere && (
                  <button type="button" onClick={handleDelete}
                    className="rounded-xl bg-rose-50 px-4 py-2 text-rose-700 hover:bg-rose-100">
                    Eliminar
                  </button>
                )}
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={onClose}
                  className="rounded-xl px-4 py-2 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50">
                  {event && !isEditing ? 'Cerrar' : 'Cancelar'}
                </button>

                {event && !isEditing ? (
                  <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsEditing(true); }}
                    className="rounded-xl px-4 py-2 bg-blue-600 text-white shadow-lg hover:shadow-xl">
                    Editar
                  </button>
                ) : (
                  <button type="submit" disabled={loading}
                    className="rounded-xl px-4 py-2 bg-blue-600 text-white shadow-lg hover:shadow-xl disabled:opacity-50">
                    {loading ? '...' : 'Guardar'}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
    Componente Toolbar de BÃºsqueda y Filtros
========================================================= */
const SearchFilterToolbar = React.memo(({ searchTerm, setSearchTerm, statusFilter, setStatusFilter, onExportCSV, onExportPDF }) => (
  <div className="mb-4 p-4 bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col md:flex-row gap-4 items-center justify-between">

    {/* BÃºsqueda */}
    <div className="relative w-full md:w-96">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <svg className="h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>
      <input
        type="text"
        className="block w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg leading-5 bg-white placeholder-slate-500 focus:outline-none focus:placeholder-slate-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition duration-150 ease-in-out"
        placeholder="Buscar por ID, monto, nota..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
    </div>

    {/* Filtros y ExportaciÃ³n */}
    <div className="flex items-center gap-3 w-full md:w-auto overflow-x-auto">

      {/* Filtro de Estado */}
      <select
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        className="block w-full md:w-48 pl-3 pr-10 py-2 text-base border-slate-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-lg"
      >
        <option value="all">Todos los estados</option>
        <option value="pending">â³ Pendientes</option>
        <option value="complete">âœ… Completos (Sin confirmar)</option>
        <option value="confirmed">ðŸ‘® Confirmados</option>
        <option value="incomplete">âŒ Incompletos</option>
      </select>

      {/* Botones de ExportaciÃ³n */}
      <div className="flex items-center gap-2 border-l pl-3 ml-1 border-slate-200">
        <button
          onClick={onExportCSV}
          className="p-2 text-slate-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
          title="Exportar a Excel/CSV"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
        </button>
        <button
          onClick={onExportPDF}
          className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          title="Exportar a PDF"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        </button>
      </div>
    </div>
  </div>
));

export default function CortesPage() {
  const [allEvents, setAllEvents] = useState([]);
  const [eventsBySucursal, setEventsBySucursal] = useState([]);
  // ðŸŸ¢ NUEVO: Estados para filtros y bÃºsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSucursal, setSelectedSucursal] = useState('todas');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [slotInfo, setSlotInfo] = useState(null);
  const [error, setError] = useState('');
  const [resumenSucursal, setResumenSucursal] = useState([]);
  const [resumenTotal, setResumenTotal] = useState(null);
  const [showMoreEvents, setShowMoreEvents] = useState(null); // Para mostrar todos los eventos de un dÃ­a

  const filterEventsBySucursal = useCallback((events, sucursal) => {
    if (sucursal === 'todas') {
      setEventsBySucursal(events);
    } else {
      setEventsBySucursal(events.filter(e => {
        const s = Array.isArray(e.sucursal) ? e.sucursal[0] : e.sucursal;
        return (s || '').toLowerCase() === sucursal.toLowerCase();
      }));
    }
  }, []);

  const handleSucursalChange = useCallback((newSucursal) => {
    setSelectedSucursal(newSucursal);
    filterEventsBySucursal(allEvents, newSucursal);
  }, [allEvents, filterEventsBySucursal]);

  const goToPrevious = useCallback(() => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  }, [currentDate]);

  const goToNext = useCallback(() => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  }, [currentDate]);

  const goToToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  const fetchResumenes = useCallback(async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const [sucursalData, totalData] = await Promise.all([
        getResumenPorSucursal(year, month),
        getResumenTotal(year, month)
      ]);
      setResumenSucursal(sucursalData);
      setResumenTotal(totalData);
    } catch (err) { console.error(err); }
  }, [currentDate]);

  const fetchEvents = async () => {
    setError('');
    try {
      const data = await getCortes();
      const formatted = data.map((corte) => {
        const base = corte.dia ? parseISO(corte.dia) : parseISO(corte.fecha);
        const start = new Date(base.getFullYear(), base.getMonth(), base.getDate());
        const end = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 23, 59, 59);
        return {
          ...corte,
          start, end,
          title: 'Corte',
          cantidad_corte: corte.cantidad,
          notas: corte.comentario,
          sucursal: Array.isArray(corte.sucursal) ? corte.sucursal[0] : corte.sucursal,
          formas_pago: corte.formas_pago,
          estado_contador: corte.estado_contador || 'pendiente',
          creado_por_nombre: corte.creado_por_nombre || null,
          confirmado_por_nombre: corte.confirmado_por_nombre || null,
          confirmado_en: corte.confirmado_en || null,
        };
      });
      setAllEvents(formatted);
      filterEventsBySucursal(formatted, selectedSucursal);
    } catch (err) {
      console.error(err);
      setError('No se pudieron cargar los cortes. Verifica tu conexión.');
    }
  };

  useEffect(() => {
    fetchEvents();
    fetchResumenes();
  }, []);

  useEffect(() => {
    fetchResumenes();
  }, [currentDate, fetchResumenes]);

  // ðŸŸ¢ NUEVO: Filtrar eventos por bÃºsqueda y estado
  const finalFilteredEvents = useMemo(() => {
    return eventsBySucursal.filter(event => {
      // Filtro por texto (ID, monto, notas, sucursal)
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        event.id.toString().includes(searchLower) ||
        (event.cantidad_corte || event.cantidad || 0).toString().includes(searchLower) ||
        (event.sucursal || '').toLowerCase().includes(searchLower) ||
        (event.notas || '').toLowerCase().includes(searchLower) ||
        (event.comentario || '').toLowerCase().includes(searchLower);

      if (!matchesSearch) return false;

      // Filtro por estado
      if (statusFilter === 'all') return true;
      if (statusFilter === 'confirmed') return event.estado_contador === 'confirmado';
      if (statusFilter === 'pending') return event.estado_contador === 'pendiente';
      if (statusFilter === 'complete') return event.estado === 'completo' && event.estado_contador !== 'confirmado';
      if (statusFilter === 'incomplete') return event.estado === 'incompleto';

      return true;
    });
  }, [eventsBySucursal, searchTerm, statusFilter]);

  // ðŸŸ¢ NUEVO: FunciÃ³n para exportar a CSV
  const handleExportCSV = () => {
    if (finalFilteredEvents.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    const headers = ['ID', 'Fecha', 'Sucursal', 'Monto', 'Estado', 'Estado Contador', 'Notas'];
    const rows = finalFilteredEvents.map(e => [
      e.id,
      format(new Date(e.start), 'dd/MM/yyyy'),
      e.sucursal,
      e.cantidad_corte || e.cantidad,
      e.estado,
      e.estado_contador,
      (e.notas || e.comentario || '').replace(/(\r\n|\n|\r)/gm, " ")
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `cortes_export_${format(new Date(), 'yyyyMMdd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ðŸŸ¢ NUEVO: FunciÃ³n para exportar a PDF
  const handleExportPDF = () => {
    if (finalFilteredEvents.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.text('Reporte de Cortes', 14, 22);

    doc.setFontSize(11);
    doc.text(`Fecha de reporte: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, 14, 30);
    doc.text(`Total de registros: ${finalFilteredEvents.length}`, 14, 36);

    const tableColumn = ['ID', 'Fecha', 'Sucursal', 'Monto', 'Estado', 'Contador'];
    const tableRows = finalFilteredEvents.map(e => [
      e.id,
      format(new Date(e.start), 'dd/MM/yyyy'),
      e.sucursal,
      `$${Number(e.cantidad_corte || e.cantidad).toFixed(2)}`,
      e.estado,
      e.estado_contador
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 40,
      theme: 'grid',
      styles: { fontSize: 8 },
      headStyles: { fillColor: [22, 163, 74] } // Green-600
    });

    doc.save(`cortes_report_${format(new Date(), 'yyyyMMdd')}.pdf`);
  };

  const handleSelectSlot = () => { };
  const handleSelectEvent = (event) => { setSelectedEvent(event); setSlotInfo(null); setIsModalOpen(true); };
  const handleSaveCut = () => {
    setIsModalOpen(false);
    fetchEvents();
    fetchResumenes();
  };

  // 🟢 CALCULAR TOTALES POR DÍA
  const dailyTotals = useMemo(() => {
    const totals = {};
    finalFilteredEvents.forEach(evt => {
      const dateKey = format(evt.start, 'yyyy-MM-dd');
      const amount = Number(evt.cantidad_corte ?? evt.cantidad) || 0;
      totals[dateKey] = (totals[dateKey] || 0) + amount;
    });
    return totals;
  }, [finalFilteredEvents]);

  // 🟢 COMPONENTE CUSTOM HEADER
  const CustomDateHeader = useCallback(({ date, label }) => {
    const dateKey = format(date, 'yyyy-MM-dd');
    const total = dailyTotals[dateKey] || 0;
    return (
      <div className="flex flex-col items-start pl-1">
        <span className="text-sm font-semibold text-slate-600">{label}</span>
        {total > 0 && (
          <div className="text-xs font-bold text-emerald-600 mt-0.5 bg-emerald-50 px-1.5 py-0.5 rounded-md">
            ${total.toLocaleString('es-MX')}
          </div>
        )}
      </div>
    );
  }, [dailyTotals]);


  const OpenCutButton = () => (
    <button
      onClick={() => { setSelectedEvent(null); setSlotInfo({ start: new Date() }); setIsModalOpen(true); }}
      className="fixed bottom-8 right-8 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-xl hover:bg-blue-700 hover:shadow-2xl transition-all transform hover:scale-105 active:scale-95"
    >
      <IconAdd className="h-8 w-8" />
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 md:p-8 font-sans text-slate-900">
      <div className="mx-auto max-w-7xl space-y-6">

        {/* Header Principal */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">Cortes de Caja</h1>
            <p className="text-slate-500 mt-1">Gestiona y supervisa los cortes diarios por sucursal</p>
          </div>
          <div className="flex items-center gap-3 bg-white p-1.5 rounded-2xl border border-slate-200 shadow-sm">
            <button onClick={goToPrevious} className="p-2 hover:bg-slate-50 rounded-xl text-slate-600 transition-colors">
              <IconChevronLeft />
            </button>
            <span className="text-lg font-bold text-slate-800 min-w-[140px] text-center capitalize">
              {format(currentDate, 'MMMM yyyy', { locale: es })}
            </span>
            <button onClick={goToNext} className="p-2 hover:bg-slate-50 rounded-xl text-slate-600 transition-colors">
              <IconChevronRight />
            </button>
            <div className="w-px h-6 bg-slate-200 mx-1"></div>
            <button onClick={goToToday} className="px-4 py-1.5 text-sm font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors">
              Hoy
            </button>
          </div>
        </div>

        {/* 🟢 NUEVO: Toolbar de Búsqueda y Filtros */}
        <SearchFilterToolbar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          onExportCSV={handleExportCSV}
          onExportPDF={handleExportPDF}
        />

        {/* Filtros y Resumen */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm font-semibold text-slate-700">📍 Sucursal:</span>
              {['todas', 'la fe', 'expo', 'colon', 'torreon', 'pablo', 'reynosa', 'tapachula'].map(s => (
                <button key={s}
                  onClick={() => handleSucursalChange(s)}
                  className={`px-3 py-1.5 rounded-full text-sm border shadow-sm transition
                  ${selectedSucursal === s ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}>
                  {s === 'todas' ? 'Todas' : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Tarjetas de Resumen */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 pt-2 border-t border-slate-100">
            {/* Total General */}
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
              <p className="text-xs text-slate-500 font-medium uppercase">Total Mes</p>
              <p className="text-lg font-bold text-slate-900">
                ${Number(resumenTotal?.total || 0).toLocaleString('es-MX')}
              </p>
              <div className="flex gap-2 text-[10px] mt-1">
                <span className="text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                  {resumenTotal?.cortes_completos || 0} OK
                </span>
                <span className="text-red-600 bg-red-50 px-1.5 py-0.5 rounded">
                  {resumenTotal?.cortes_incompletos || 0} Pend
                </span>
              </div>
            </div>

            {/* Por Sucursal */}
            {resumenSucursal.map((r, i) => {
              const sucName = (r.sucursal || '').toLowerCase();
              const colors = sucursalColors[sucName] || sucursalColors.default;
              return (
                <div key={i} className="rounded-xl p-3 border" style={{ backgroundColor: colors.bg, borderColor: colors.bg }}>
                  <p className="text-xs font-bold uppercase truncate" style={{ color: colors.text }}>
                    {r.sucursal}
                  </p>
                  <p className="text-lg font-bold mt-0.5" style={{ color: colors.text }}>
                    ${Number(r.total || 0).toLocaleString('es-MX')}
                  </p>
                  <div className="flex gap-1 mt-1">
                    {parseInt(r.cortes_incompletos) > 0 && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-white/50 text-red-600">
                        {r.cortes_incompletos} ⚠️
                      </span>
                    )}
                    {parseInt(r.cortes_pendientes) > 0 && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-white/50 text-amber-600">
                        {r.cortes_pendientes} ⏳
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {error && (
          <div className="rounded-xl bg-red-50 p-4 text-red-700 border border-red-100 flex items-center gap-3">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}

        {/* Calendario */}
        <div className="h-[750px] bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden relative">
          <Calendar
            localizer={localizer}
            events={finalFilteredEvents}
            startAccessor="start"
            endAccessor="end"
            date={currentDate}
            onNavigate={(date) => setCurrentDate(date)}
            view="month"
            onView={() => { }}
            culture='es' // 🟢 Asegurar idioma español
            components={{
              event: CustomEvent,
              toolbar: () => null, // Usamos nuestro propio header
              month: {
                dateHeader: CustomDateHeader, // 🟢 Custom Header con Total
              }
            }}
            formats={{
              weekdayFormat: (date, culture, localizer) =>
                localizer.format(date, 'EEEE', culture).toUpperCase() // 🟢 LUNES, MARTES...
            }}
            messages={{
              next: "Siguiente",
              previous: "Anterior",
              today: "Hoy",
              month: "Mes",
              week: "Semana",
              day: "Día",
              showMore: total => `+${total} más`,
            }}
            selectable
            onSelectSlot={() => { }}
            onSelectEvent={(e) => { setSelectedEvent(e); setSlotInfo(null); setIsModalOpen(true); }}
            onShowMore={(events, date) => setShowMoreEvents({ events, date })}
            eventPropGetter={() => ({ style: { background: 'transparent', border: 'none', padding: 0 } })}
          />

          {/* Botón flotante para agregar en el día actual si no hay selección */}
          <div className="absolute bottom-6 right-6">
            {/* El botón flotante principal está fuera del calendario, este es solo un placeholder si se necesitara */}
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <CutModal
          event={selectedEvent}
          slotInfo={slotInfo}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveCut}
          onRefreshEvents={fetchEvents}
          selectedSucursal={selectedSucursal}
        />
      )}

      {/* Botón flotante */}

      {/* Popup para mostrar todos los eventos de un dÃ­a */}
      {showMoreEvents && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" onClick={() => setShowMoreEvents(null)}>
          <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-slate-900">
                Cortes del {format(showMoreEvents.date, 'EEEE d MMMM yyyy', { locale: es })}
              </h3>
              <button onClick={() => setShowMoreEvents(null)} className="text-slate-400 hover:text-slate-600">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {showMoreEvents.events.map((event, idx) => (
                <div
                  key={idx}
                  className="p-4 rounded-xl border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => {
                    setSelectedEvent(event);
                    setSlotInfo(null);
                    setIsModalOpen(true);
                    setShowMoreEvents(null);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <SucursalPill
                        sucursal={event.sucursal}
                        monto={event.cantidad_corte ?? event.cantidad}
                        estado={event.estado}
                        estado_contador={event.estado_contador}
                        notas={event.notas}
                      />
                    </div>
                    <span className="text-sm text-slate-500">ID: {event.id}</span>
                  </div>
                  {event.notas && (
                    <p className="text-xs text-slate-600 mt-2 line-clamp-2">{event.notas}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <OpenCutButton />

      {/* THEME MODERNO & GRID LINES */}
      <style>{`
        /* Toolbar */
        .zw-toolbar{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:12px;margin-bottom:12px;background:linear-gradient(180deg,#F8FAFF,#F4F7FF);border:1px solid rgba(15,23,42,0.06);border-radius:16px;box-shadow:0 10px 30px rgba(2,6,23,.06);}
        
        /* Calendar Header */
        .rbc-header {
          padding: 12px 0;
          font-weight: 800; /* Más negrita */
          color: #334155;
          background-color: #F8FAFC;
          border-bottom: 2px solid #E2E8F0;
          text-transform: uppercase;
          font-size: 0.8rem;
          letter-spacing: 0.05em;
        }
        
        /* Grid Lines & Cells */
        .rbc-month-view {
          border: 1px solid #E2E8F0;
          border-radius: 0 0 16px 16px;
        }
        .rbc-day-bg {
          border-left: 1px solid #E2E8F0;
        }
        .rbc-day-bg + .rbc-day-bg {
          border-left: 1px solid #E2E8F0;
        }
        .rbc-month-row {
          border-top: 1px solid #E2E8F0;
          min-height: 120px; /* 🟢 Celdas más altas */
        }
        .rbc-off-range-bg {
          background-color: #F8FAFC;
        }
        .rbc-today {
          background-color: #F0F9FF; /* Light blue for today */
        }
        
        /* Date Number */
        .rbc-date-cell {
          padding: 8px;
          font-weight: 600;
          font-size: 0.9rem;
          color: #64748B;
        }
        .rbc-now .rbc-date-cell {
          color: #2563EB; /* Blue for today's number */
        }

        /* Events Container */
        .rbc-event {
          background: transparent !important;
          padding: 2px 4px !important;
        }
        
        /* Scrollbar */
        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        
        /* Animations */
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
      `}</style>
    </div>
  );
}
